import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  appendBillingResult,
  billingFallbackPath,
  isPlausibleWorkspaceId,
  resolveReturnTo,
  toSameOriginHref,
  type BillingKind,
  type BillingResult,
} from '../utils/billingReturn';
import { sameWorkspaceId } from '../utils/workspaceId';

/**
 * Central `/billing/return` — where Stripe Checkout sends the browser back.
 *
 * Contract: see utils/billingReturn.ts. This page:
 *   1. shows an accurate result (it never claims a plan changed when it
 *      could not see the change),
 *   2. for a successful plan checkout, best-effort polls
 *      GET /api/subscription/me (read-only; the webhook is the writer) to
 *      confirm the new plan,
 *   3. sends the browser to the VALIDATED `return_to` with
 *      `billing=success|cancel` appended, or to the workspace's own
 *      billing / credits page when `return_to` is missing or not allowed.
 */

interface BillingReturnPageProps {
  /** Test seams; production uses the defaults. */
  pollIntervalMs?: number;
  pollAttempts?: number;
  redirectDelayMs?: number;
}

type Confirmation = 'pending' | 'confirmed' | 'unconfirmed' | 'scheduled' | 'not_applicable';

const KINDS: BillingKind[] = ['plan', 'plan_change', 'credits'];

async function getSessionToken(): Promise<string | null> {
  try {
    const stored = window.localStorage.getItem('admin_token');
    if (stored) return stored;
  } catch {
    // storage unavailable — fall through to the session bridge
  }
  try {
    const res = await fetch('/api/auth/session/bridge', { credentials: 'include' });
    if (!res.ok) return null;
    const data = await res.json();
    return typeof data?.accessToken === 'string' ? data.accessToken : null;
  } catch {
    return null;
  }
}

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export function BillingReturnPage({
  pollIntervalMs = 1500,
  pollAttempts = 5,
  redirectDelayMs = 2500,
}: BillingReturnPageProps = {}) {
  const [searchParams] = useSearchParams();
  const rawStatus = searchParams.get('status');
  const status: BillingResult | null = rawStatus === 'success' || rawStatus === 'cancel' ? rawStatus : null;
  const rawKind = searchParams.get('kind');
  const kind: BillingKind = KINDS.includes(rawKind as BillingKind) ? (rawKind as BillingKind) : 'plan';
  const rawWorkspaceId = searchParams.get('workspaceId');
  const workspaceId = isPlausibleWorkspaceId(rawWorkspaceId) ? rawWorkspaceId : null;
  const targetPlan = searchParams.get('plan');
  const returnTo = searchParams.get('return_to') || searchParams.get('returnUrl');

  const [destination, setDestination] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<Confirmation>('pending');
  const [confirmedPlanName, setConfirmedPlanName] = useState<string | null>(null);
  useEffect(() => {
    // No run-once ref: under StrictMode the first run is cancelled by its
    // cleanup and the second run does the work. Everything here is read-only.
    let cancelled = false;

    (async () => {
      // Every candidate is an ABSOLUTE, validated href (CEN-BILL-2): never a
      // bare path that could normalise to '//host'.
      const homeHref = `${window.location.origin}/home`;
      const validated = await resolveReturnTo(returnTo);
      const base = validated ?? toSameOriginHref(billingFallbackPath(workspaceId, kind)) ?? homeHref;
      const finalTarget = status ? appendBillingResult(base, status) ?? homeHref : base;

      // A plan change with no target plan was SCHEDULED for the next billing
      // cycle (PlanPicker sends `plan` only for an immediate change). The
      // workspace is still on its old plan, so there is nothing to confirm —
      // and "Your plan is updated" would be false (QA-A C-4).
      if (status === 'success' && kind === 'plan_change' && !targetPlan) {
        if (!cancelled) setConfirmation('scheduled');
      } else if (status === 'success' && (kind === 'plan' || kind === 'plan_change')) {
        // Confirmation applies only to a successful plan purchase / change.
        const token = await getSessionToken();
        let confirmed = false;
        if (token) {
          for (let attempt = 0; attempt < pollAttempts && !cancelled && !confirmed; attempt += 1) {
            try {
              const res = await fetch('/api/subscription/me', { headers: { Authorization: `Bearer ${token}` } });
              if (res.ok) {
                const data = await res.json();
                // CEN-CASE: UUID-canonical — an upper-case URL id is the same workspace.
                const sameWorkspace = !workspaceId || !data?.workspaceId || sameWorkspaceId(data.workspaceId, workspaceId);
                const planMatches = targetPlan ? data?.planCode === targetPlan : data?.status === 'active' || data?.status === 'trialing';
                if (sameWorkspace && planMatches) {
                  confirmed = true;
                  if (!cancelled) setConfirmedPlanName(typeof data.planName === 'string' ? data.planName : null);
                  break;
                }
              }
            } catch {
              // transient — keep polling within the attempt budget
            }
            if (attempt < pollAttempts - 1) await wait(pollIntervalMs);
          }
        }
        if (!cancelled) setConfirmation(confirmed ? 'confirmed' : 'unconfirmed');
      } else if (!cancelled) {
        setConfirmation('not_applicable');
      }

      if (cancelled) return;
      setDestination(finalTarget);
      // REPLACE, not assign: with this interstitial left in history, Back from
      // the destination re-ran it and it forwarded again — the user could not
      // leave (V1-CENTRAL-LOGIN, runtime-proven).
      setTimeout(() => {
        if (!cancelled) window.location.replace(finalTarget);
      }, redirectDelayMs);
    })();

    return () => {
      cancelled = true;
    };
    // The page acts once per load; inputs come from the URL it was opened with.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let heading: string;
  let detail: string;
  if (status === 'cancel') {
    heading = 'Checkout canceled';
    detail = 'No payment was taken and nothing was changed.';
  } else if (status !== 'success') {
    heading = 'Returning from billing';
    detail = 'We could not tell how checkout ended. Check your billing page to see the current state.';
  } else if (kind === 'credits') {
    heading = 'Payment received';
    detail = 'Your credits are added as soon as the payment is confirmed — usually within a minute.';
  } else if (confirmation === 'scheduled') {
    heading = 'Plan change scheduled';
    detail = 'Your new plan takes effect at the end of the current billing period.';
  } else if (confirmation === 'confirmed') {
    heading = 'Your plan is updated';
    detail = confirmedPlanName ? `This workspace is now on ${confirmedPlanName}.` : 'Your new plan is active.';
  } else if (confirmation === 'unconfirmed') {
    heading = 'Payment received';
    detail = 'Your plan will update in a moment. If it has not changed in a few minutes, check the billing page or contact support.';
  } else {
    heading = 'Confirming your plan…';
    detail = 'This only takes a moment.';
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-8 max-w-md w-full text-center" role="status">
        <h1 className="text-lg font-semibold text-gray-900">{heading}</h1>
        <p className="text-sm text-gray-600 mt-2">{detail}</p>
        {destination ? (
          <a
            href={destination}
            className="inline-block mt-6 px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
          >
            Continue
          </a>
        ) : (
          <div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mt-6" />
        )}
      </div>
    </div>
  );
}
